/**
 * AI feature hooks — TanStack Query wrappers + streaming chat hook.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, useCallback, useRef } from 'react'
import { aiApi } from './api'

// ── Status ───────────────────────────────────────────────────

export function useAIStatus() {
    return useQuery({
        queryKey: ['ai', 'status'],
        queryFn: async () => {
            const res = await aiApi.getStatus()
            return res.data
        },
    })
}

// ── Revaluate Weights (SSE streaming) ────────────────────────

export interface RevaluateProgress {
    batch: number
    total_batches: number
    domain: string
    level: number
    status: 'processing' | 'complete'
    count?: number
}

export interface WeightChange {
    id: string
    text: string
    old_weight: number
    new_weight: number
}

export interface RevaluateError {
    batch: number
    domain: string
    level: number
    message: string
}

export function useRevaluateWeights() {
    const queryClient = useQueryClient()
    const [isRunning, setIsRunning] = useState(false)
    const [progress, setProgress] = useState<RevaluateProgress | null>(null)
    const [changes, setChanges] = useState<WeightChange[]>([])
    const [errors, setErrors] = useState<RevaluateError[]>([])
    const [done, setDone] = useState(false)

    const run = useCallback(async () => {
        setIsRunning(true)
        setProgress(null)
        setChanges([])
        setErrors([])
        setDone(false)

        try {
            const body = await aiApi.revaluateWeightsStream()
            if (!body) throw new Error('No response body')

            const reader = body.getReader()
            const decoder = new window.TextDecoder()

            while (true) {
                const { done: streamDone, value } = await reader.read()
                if (streamDone) break

                const chunk = decoder.decode(value, { stream: true })
                const lines = chunk.split('\n')

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue
                    const raw = line.slice(6).trim()
                    if (!raw) continue

                    try {
                        const event = JSON.parse(raw)
                        switch (event.type) {
                            case 'progress':
                                setProgress(event)
                                break
                            case 'change':
                                setChanges(prev => [...prev, event])
                                break
                            case 'error':
                                setErrors(prev => [...prev, event])
                                break
                            case 'done':
                                setDone(true)
                                queryClient.invalidateQueries({ queryKey: ['questions'] })
                                break
                        }
                    } catch { /* skip malformed */ }
                }
            }
        } catch (err: any) {
            setErrors(prev => [...prev, { batch: 0, domain: '', level: 0, message: err.message }])
        } finally {
            setIsRunning(false)
        }
    }, [queryClient])

    const reset = useCallback(() => {
        setProgress(null)
        setChanges([])
        setErrors([])
        setDone(false)
    }, [])

    return { isRunning, progress, changes, errors, done, run, reset }
}

// ── Generate Questions ───────────────────────────────────────

export function useGenerateQuestions() {
    const [isRunning, setIsRunning] = useState(false)
    const [status, setStatus] = useState<string>('')
    const [questions, setQuestions] = useState<any[]>([])
    const [error, setError] = useState<string | null>(null)
    const [done, setDone] = useState(false)

    const run = useCallback(async (data: {
        prompt: string
        domain_id?: string
        maturity_level?: number
        question_type?: string
        count?: number
    }) => {
        setIsRunning(true)
        setStatus('starting')
        setQuestions([])
        setError(null)
        setDone(false)

        try {
            const body = await aiApi.generateQuestionsStream(data)
            if (!body) throw new Error('No response body')

            const reader = body.getReader()
            const decoder = new window.TextDecoder()

            while (true) {
                const { done: streamDone, value } = await reader.read()
                if (streamDone) break

                const chunk = decoder.decode(value, { stream: true })
                const lines = chunk.split('\n')

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue
                    const raw = line.slice(6).trim()
                    if (!raw) continue

                    try {
                        const event = JSON.parse(raw)
                        switch (event.type) {
                            case 'progress':
                                setStatus(event.status)
                                break
                            case 'question': {
                                const { type: _, ...q } = event
                                setQuestions(prev => [...prev, q])
                                break
                            }
                            case 'done':
                                setDone(true)
                                break
                            case 'error':
                                setError(event.message)
                                break
                        }
                    } catch { /* skip malformed */ }
                }
            }
        } catch (err: any) {
            setError(err.message)
        } finally {
            setIsRunning(false)
        }
    }, [])

    const reset = useCallback(() => {
        setQuestions([])
        setError(null)
        setDone(false)
        setStatus('')
    }, [])

    return { isRunning, status, questions, error, done, run, reset }
}

export function useGenerateInitiative() {
    const [isRunning, setIsRunning] = useState(false)
    const [status, setStatus] = useState<string>('')
    const [result, setResult] = useState<any>(null)
    const [error, setError] = useState<string | null>(null)

    const run = useCallback(async (data: FormData | { prompt: string; organization_id?: string }) => {
        setIsRunning(true)
        setStatus('starting')
        setResult(null)
        setError(null)

        try {
            const body = await aiApi.generateInitiativeStream(data)
            if (!body) throw new Error('No response body')

            const reader = body.getReader()
            const decoder = new window.TextDecoder()

            while (true) {
                const { done: streamDone, value } = await reader.read()
                if (streamDone) break

                const chunk = decoder.decode(value, { stream: true })
                const lines = chunk.split('\n')

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue
                    const raw = line.slice(6).trim()
                    if (!raw) continue

                    try {
                        const event = JSON.parse(raw)
                        switch (event.type) {
                            case 'progress':
                                setStatus(event.status)
                                break
                            case 'result': {
                                const { type: _t, ...initiative } = event
                                setResult(initiative)
                                break
                            }
                            case 'done':
                                setStatus('done')
                                break
                            case 'error':
                                setError(event.message)
                                break
                        }
                    } catch { /* skip malformed */ }
                }
            }
        } catch (err: any) {
            setError(err.message)
        } finally {
            setIsRunning(false)
        }
    }, [])

    const reset = useCallback(() => {
        setResult(null)
        setError(null)
        setStatus('')
    }, [])

    return { isRunning, status, result, error, run, reset }
}

// ── Streaming Chat ───────────────────────────────────────────

export interface ChatMessage {
    id: string
    role: 'user' | 'assistant'
    content: string
}

export function useAIChat() {
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [isStreaming, setIsStreaming] = useState(false)
    const abortRef = useRef<AbortController | null>(null)

    const sendMessage = useCallback(async (content: string) => {
        const userMsg: ChatMessage = {
            id: window.crypto.randomUUID(),
            role: 'user',
            content,
        }

        const updatedMessages = [...messages, userMsg]
        setMessages(updatedMessages)

        const assistantMsg: ChatMessage = {
            id: window.crypto.randomUUID(),
            role: 'assistant',
            content: '',
        }
        setMessages(prev => [...prev, assistantMsg])
        setIsStreaming(true)

        try {
            const body = await aiApi.streamChat(
                updatedMessages.map(m => ({ role: m.role, content: m.content }))
            )

            if (!body) throw new Error('No response body')

            const reader = body.getReader()
            const decoder = new window.TextDecoder()
            let accumulated = ''

            while (true) {
                const { done, value } = await reader.read()
                if (done) break

                const chunk = decoder.decode(value, { stream: true })
                const lines = chunk.split('\n')

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6).trim()
                        if (data === '[DONE]') continue
                        try {
                            const parsed = JSON.parse(data)
                            if (parsed.content) {
                                accumulated += parsed.content
                                setMessages(prev =>
                                    prev.map(m =>
                                        m.id === assistantMsg.id
                                            ? { ...m, content: accumulated }
                                            : m
                                    )
                                )
                            }
                            if (parsed.error) {
                                accumulated += `\n\n⚠️ Error: ${parsed.error}`
                                setMessages(prev =>
                                    prev.map(m =>
                                        m.id === assistantMsg.id
                                            ? { ...m, content: accumulated }
                                            : m
                                    )
                                )
                            }
                        } catch {
                            // Skip malformed JSON chunks
                        }
                    }
                }
            }
        } catch (err: any) {
            setMessages(prev =>
                prev.map(m =>
                    m.id === assistantMsg.id
                        ? { ...m, content: `⚠️ Failed to get AI response: ${err.message}` }
                        : m
                )
            )
        } finally {
            setIsStreaming(false)
        }
    }, [messages])

    const clearMessages = useCallback(() => {
        setMessages([])
    }, [])

    const stop = useCallback(() => {
        abortRef.current?.abort()
        setIsStreaming(false)
    }, [])

    return { messages, isStreaming, sendMessage, clearMessages, stop }
}
